import React, { useState, useEffect, useRef, useMemo } from 'react';
import './LocationSelector.css';
import { 
  IconMapPin, 
  IconChevronDown, 
  IconSearch, 
  IconCheck, 
  IconClose, 
  IconGlobe 
} from './Icons';

export function formatDistrictName(name) {
  if (!name) return '';
  const SPECIAL_NAMES = {
    spsr_nellore: 'SPSR Nellore',
    y_s_r: 'YSR Kadapa',
    ntr: 'NTR',
    the_nilgirish: 'The Nilgiris',
    dakshin_kannad: 'Dakshina Kannada',
    uttar_kannad: 'Uttara Kannada',
    bengaluru_urban: 'Bengaluru Urban',
    bengaluru_rural: 'Bengaluru Rural',
    ranga_reddi: 'Ranga Reddy',
    medchal_malkajgiri: 'Medchal Malkajgiri',
    jayashankar_bhupalapally: 'Jayashankar Bhupalpally',
    komaram_bheem_asifabad: 'Komaram Bheem Asifabad',
    bhadradri_kothagudem: 'Bhadradri Kothagudem',
    yadadri_bhuvanagiri: 'Yadadri Bhuvanagiri',
    jogulamba_gadwal: 'Jogulamba Gadwal',
    rajanna_sircilla: 'Rajanna Sircilla',
  };
  const key = name.toLowerCase().trim();
  if (SPECIAL_NAMES[key]) return SPECIAL_NAMES[key];
  return name
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

export const STATE_BADGES = {
  tamilnadu: 'TN',
  andhrapradesh: 'AP',
  karnataka: 'KA',
  kerala: 'KL',
  telangana: 'TG',
};

const FALLBACK_REGIONS = {
  all: null,
  states: [
    {
      code: 'tamilnadu',
      name: 'Tamil Nadu (All Districts)',
      districts: [
        { code: 'ariyalur', name: 'Ariyalur' },
        { code: 'chengalpattu', name: 'Chengalpattu' },
        { code: 'chennai', name: 'Chennai' },
        { code: 'coimbatore', name: 'Coimbatore' },
        { code: 'cuddalore', name: 'Cuddalore' },
        { code: 'dharmapuri', name: 'Dharmapuri' },
        { code: 'dindigul', name: 'Dindigul' },
        { code: 'erode', name: 'Erode' },
        { code: 'kallakurichi', name: 'Kallakurichi' },
        { code: 'kanchipuram', name: 'Kanchipuram' },
        { code: 'kanniyakumari', name: 'Kanniyakumari' },
        { code: 'karur', name: 'Karur' },
        { code: 'krishnagiri', name: 'Krishnagiri' },
        { code: 'madurai', name: 'Madurai' },
        { code: 'mayiladuthurai', name: 'Mayiladuthurai' },
        { code: 'nagapattinam', name: 'Nagapattinam' },
        { code: 'namakkal', name: 'Namakkal' },
        { code: 'perambalur', name: 'Perambalur' },
        { code: 'pudukkottai', name: 'Pudukkottai' },
        { code: 'ramanathapuram', name: 'Ramanathapuram' },
        { code: 'ranipet', name: 'Ranipet' },
        { code: 'salem', name: 'Salem' },
        { code: 'sivaganga', name: 'Sivaganga' },
        { code: 'tenkasi', name: 'Tenkasi' },
        { code: 'thanjavur', name: 'Thanjavur' },
        { code: 'the_nilgirish', name: 'The Nilgiris' },
        { code: 'theni', name: 'Theni' },
        { code: 'thiruvallur', name: 'Thiruvallur' },
        { code: 'thiruvarur', name: 'Thiruvarur' },
        { code: 'tiruchirappalli', name: 'Tiruchirappalli' },
        { code: 'tirunelveli', name: 'Tirunelveli' },
        { code: 'tirupathur', name: 'Tirupathur' },
        { code: 'tirupur', name: 'Tirupur' },
        { code: 'tiruvannamalai', name: 'Tiruvannamalai' },
        { code: 'tuticorin', name: 'Tuticorin' },
        { code: 'vellore', name: 'Vellore' },
        { code: 'villupuram', name: 'Villupuram' },
        { code: 'virudhunagar', name: 'Virudhunagar' }
      ]
    },
    {
      code: 'andhrapradesh',
      name: 'Andhra Pradesh (All Districts)',
      districts: [
        { code: 'alluri_sitharama_raju', name: 'Alluri Sitharama Raju' },
        { code: 'anakapalli', name: 'Anakapalli' },
        { code: 'ananthapur', name: 'Ananthapur' },
        { code: 'annamayya', name: 'Annamayya' },
        { code: 'bapatla', name: 'Bapatla' },
        { code: 'chitoor', name: 'Chitoor' },
        { code: 'east_godavari', name: 'East Godavari' },
        { code: 'eluru', name: 'Eluru' },
        { code: 'guntur', name: 'Guntur' },
        { code: 'kakinada', name: 'Kakinada' },
        { code: 'konaseema', name: 'Konaseema' },
        { code: 'krishna', name: 'Krishna' },
        { code: 'kurnool', name: 'Kurnool' },
        { code: 'nandyal', name: 'Nandyal' },
        { code: 'ntr', name: 'NTR' },
        { code: 'palnadu', name: 'Palnadu' },
        { code: 'parvathipuram_manyam', name: 'Parvathipuram Manyam' },
        { code: 'prakasam', name: 'Prakasam' },
        { code: 'spsr_nellore', name: 'SPSR Nellore' },
        { code: 'sri_sathya_sai', name: 'Sri Sathya Sai' },
        { code: 'srikakulam', name: 'Srikakulam' },
        { code: 'tirupati', name: 'Tirupati' },
        { code: 'visakhapatnam', name: 'Visakhapatnam' },
        { code: 'vizianagaram', name: 'Vizianagaram' },
        { code: 'west_godavari', name: 'West Godavari' },
        { code: 'y_s_r', name: 'YSR Kadapa' }
      ]
    },
    {
      code: 'karnataka',
      name: 'Karnataka (All Districts)',
      districts: [
        { code: 'bagalkot', name: 'Bagalkot' },
        { code: 'ballari', name: 'Ballari' },
        { code: 'belagavi', name: 'Belagavi' },
        { code: 'bengaluru_rural', name: 'Bengaluru Rural' },
        { code: 'bengaluru_urban', name: 'Bengaluru Urban' },
        { code: 'bidar', name: 'Bidar' },
        { code: 'chamarajnagar', name: 'Chamarajnagar' },
        { code: 'chikballapur', name: 'Chikballapur' },
        { code: 'chikkamagaluru', name: 'Chikkamagaluru' },
        { code: 'chitradurga', name: 'Chitradurga' },
        { code: 'dakshin_kannad', name: 'Dakshina Kannada' },
        { code: 'davangere', name: 'Davangere' },
        { code: 'dharwad', name: 'Dharwad' },
        { code: 'gadag', name: 'Gadag' },
        { code: 'hassan', name: 'Hassan' },
        { code: 'haveri', name: 'Haveri' },
        { code: 'kalaburagi', name: 'Kalaburagi' },
        { code: 'kodagu', name: 'Kodagu' },
        { code: 'kolar', name: 'Kolar' },
        { code: 'koppal', name: 'Koppal' },
        { code: 'mandya', name: 'Mandya' },
        { code: 'mysuru', name: 'Mysuru' },
        { code: 'raichur', name: 'Raichur' },
        { code: 'ramanagara', name: 'Ramanagara' },
        { code: 'shivamogga', name: 'Shivamogga' },
        { code: 'tumakuru', name: 'Tumakuru' },
        { code: 'udupi', name: 'Udupi' },
        { code: 'uttar_kannad', name: 'Uttara Kannada' },
        { code: 'vijayanagar', name: 'Vijayanagar' },
        { code: 'vijayapura', name: 'Vijayapura' },
        { code: 'yadgir', name: 'Yadgir' }
      ]
    },
    {
      code: 'kerala',
      name: 'Kerala (All Districts)',
      districts: [
        { code: 'alappuzha', name: 'Alappuzha' },
        { code: 'ernakulam', name: 'Ernakulam' },
        { code: 'idukki', name: 'Idukki' },
        { code: 'kannur', name: 'Kannur' },
        { code: 'kasaragod', name: 'Kasaragod' },
        { code: 'kollam', name: 'Kollam' },
        { code: 'kottayam', name: 'Kottayam' },
        { code: 'malappuram', name: 'Malappuram' },
        { code: 'palakkad', name: 'Palakkad' },
        { code: 'pathanamthipta', name: 'Pathanamthitta' },
        { code: 'thiruvananthapuram', name: 'Thiruvananthapuram' },
        { code: 'thrissur', name: 'Thrissur' },
        { code: 'wayanad', name: 'Wayanad' }
      ]
    },
    {
      code: 'telangana',
      name: 'Telangana (All Districts)',
      districts: [
        { code: 'adilabad', name: 'Adilabad' },
        { code: 'bhadradri_kothagudem', name: 'Bhadradri Kothagudem' },
        { code: 'hanumakonda', name: 'Hanumakonda' },
        { code: 'hyderabad', name: 'Hyderabad' },
        { code: 'jagitial', name: 'Jagitial' },
        { code: 'jangoan', name: 'Jangoan' },
        { code: 'jayashankar_bhupalapally', name: 'Jayashankar Bhupalpally' },
        { code: 'jogulamba_gadwal', name: 'Jogulamba Gadwal' },
        { code: 'kamareddy', name: 'Kamareddy' },
        { code: 'karimnagar', name: 'Karimnagar' },
        { code: 'khammam', name: 'Khammam' },
        { code: 'komaram_bheem_asifabad', name: 'Komaram Bheem Asifabad' },
        { code: 'mahabubabad', name: 'Mahabubabad' },
        { code: 'mahabubnagar', name: 'Mahabubnagar' },
        { code: 'mancherial', name: 'Mancherial' },
        { code: 'medak', name: 'Medak' },
        { code: 'medchal_malkajgiri', name: 'Medchal Malkajgiri' },
        { code: 'mulugu', name: 'Mulugu' },
        { code: 'nagarkurnool', name: 'Nagarkurnool' },
        { code: 'nalgonda', name: 'Nalgonda' },
        { code: 'narayanpet', name: 'Narayanpet' },
        { code: 'nirmal', name: 'Nirmal' },
        { code: 'nizamabad', name: 'Nizamabad' },
        { code: 'peddapalli', name: 'Peddapalli' },
        { code: 'rajanna_sircilla', name: 'Rajanna Sircilla' },
        { code: 'ranga_reddi', name: 'Ranga Reddy' },
        { code: 'sangareddy', name: 'Sangareddy' },
        { code: 'siddipet', name: 'Siddipet' },
        { code: 'suryapet', name: 'Suryapet' },
        { code: 'vikarabad', name: 'Vikarabad' },
        { code: 'wanaparthy', name: 'Wanaparthy' },
        { code: 'warangal', name: 'Warangal' },
        { code: 'yadadri_bhuvanagiri', name: 'Yadadri Bhuvanagiri' }
      ]
    }
  ]
};

export default function LocationSelector({
  selectedDistrict = 'tamilnadu',
  onSelectDistrict,
  regions = { all: null, states: [] },
  currentDistrictName = ''
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('tamilnadu');
  const [focusedIndex, setFocusedIndex] = useState(-1);

  const containerRef = useRef(null);
  const searchInputRef = useRef(null);

  // Guarantee valid regions data even if initial network fetch is pending or reconnecting
  const activeRegions = useMemo(() => {
    if (regions && regions.states && regions.states.length > 0) {
      return {
        all: regions.all || null,
        states: regions.states,
      };
    }
    return FALLBACK_REGIONS;
  }, [regions]);

  // Sync active tab with selectedDistrict on mount / change
  useEffect(() => {
    if (selectedDistrict === 'all') {
      setActiveTab('all');
    } else {
      const isState = (activeRegions.states || []).some((st) => st.code.toLowerCase() === selectedDistrict.toLowerCase());
      if (isState) {
        setActiveTab(selectedDistrict.toLowerCase());
      } else {
        for (const st of activeRegions.states || []) {
          if ((st.districts || []).some((d) => d.code.toLowerCase() === selectedDistrict.toLowerCase())) {
            setActiveTab(st.code);
            break;
          }
        }
      }
    }
  }, [selectedDistrict, activeRegions]);

  // Close on outside click
  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleOutsideClick);
      document.addEventListener('touchstart', handleOutsideClick);
    }
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
      document.removeEventListener('touchstart', handleOutsideClick);
    };
  }, [isOpen]);

  // Focus search input when popover opens
  useEffect(() => {
    if (isOpen) {
      setSearchQuery('');
      setFocusedIndex(-1);
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 50);
    }
  }, [isOpen]);

  const handleSelect = (code) => {
    if (onSelectDistrict) {
      onSelectDistrict(code);
    }
    setIsOpen(false);
  };

  // Trigger button label
  const triggerLabel = useMemo(() => {
    if (selectedDistrict === 'all') return 'India (All States)';
    const matchingState = (activeRegions.states || []).find((s) => s.code.toLowerCase() === selectedDistrict.toLowerCase());
    if (matchingState) {
      const cleanName = matchingState.name.replace(' (All Districts)', '');
      return `${cleanName} (State)`;
    }
    for (const s of activeRegions.states || []) {
      const d = (s.districts || []).find((item) => item.code.toLowerCase() === selectedDistrict.toLowerCase());
      if (d) {
        const badge = STATE_BADGES[s.code.toLowerCase()] || s.name.replace(' (All Districts)', '');
        return `${formatDistrictName(d.name)}, ${badge}`;
      }
    }
    return currentDistrictName || selectedDistrict;
  }, [selectedDistrict, activeRegions, currentDistrictName]);

  const triggerBadge = useMemo(() => {
    if (selectedDistrict === 'all') return 'NAT';
    if (STATE_BADGES[selectedDistrict.toLowerCase()]) {
      return STATE_BADGES[selectedDistrict.toLowerCase()];
    }
    for (const s of activeRegions.states || []) {
      const d = (s.districts || []).find((item) => item.code.toLowerCase() === selectedDistrict.toLowerCase());
      if (d) return STATE_BADGES[s.code.toLowerCase()] || s.code.slice(0, 2).toUpperCase();
    }
    return 'IN';
  }, [selectedDistrict, activeRegions]);

  // Search Results across all states & districts
  const searchResults = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return null;

    const list = [];
    (activeRegions.states || []).forEach((st) => {
      const stNameClean = st.name.replace(' (All Districts)', '');
      if (st.name.toLowerCase().includes(q) || st.code.toLowerCase().includes(q)) {
        list.push({ code: st.code, name: `All ${stNameClean}`, state: `${stNameClean} (Entire State)`, type: 'state' });
      }
      (st.districts || []).forEach((d) => {
        const dClean = formatDistrictName(d.name);
        if (d.name.toLowerCase().includes(q) || dClean.toLowerCase().includes(q) || d.code.toLowerCase().includes(q)) {
          list.push({ code: d.code, name: dClean, state: stNameClean, type: 'district' });
        }
      });
    });

    return list;
  }, [searchQuery, activeRegions]);

  // Keyboard Navigation
  const handleKeyDown = (e) => {
    if (!isOpen) {
      if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
        e.preventDefault();
        setIsOpen(true);
      }
      return;
    }

    if (e.key === 'Escape') {
      e.preventDefault();
      setIsOpen(false);
      return;
    }

    if (searchResults && searchResults.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setFocusedIndex((prev) => (prev + 1) % searchResults.length);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setFocusedIndex((prev) => (prev <= 0 ? searchResults.length - 1 : prev - 1));
      } else if (e.key === 'Enter') {
        e.preventDefault();
        if (focusedIndex >= 0 && focusedIndex < searchResults.length) {
          handleSelect(searchResults[focusedIndex].code);
        }
      }
    }
  };

  // Active state data for browsing view
  const currentActiveState = useMemo(() => {
    return (activeRegions.states || []).find((s) => s.code === activeTab) || (activeRegions.states || [])[0] || null;
  }, [activeTab, activeRegions]);

  return (
    <div 
      className={`location-selector-hub ${isOpen ? 'is-active' : ''}`}
      ref={containerRef}
      onKeyDown={handleKeyDown}
    >
      {/* ✦ Header Trigger Button ✦ */}
      <button
        type="button"
        className="location-trigger-btn"
        onClick={() => setIsOpen((prev) => !prev)}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-label={`Selected market scope: ${triggerLabel}`}
        title={`Change location or market scope (Currently: ${currentDistrictName})`}
      >
        <span className="location-pin-icon-wrap">
          {selectedDistrict === 'all' ? (
            <IconGlobe width={13} height={13} />
          ) : (
            <IconMapPin width={13} height={13} />
          )}
        </span>
        <span className="location-trigger-text" title={triggerLabel}>
          {triggerLabel}
        </span>
        <span className="location-scope-tag">{triggerBadge}</span>
        <span className={`location-chevron-wrap ${isOpen ? 'rotated' : ''}`}>
          <IconChevronDown width={11} height={11} />
        </span>
      </button>

      {/* ✦ Modern Two-Pane Master-Detail Popover ✦ */}
      {isOpen && (
        <div className="location-popover-dropdown" role="dialog" aria-modal="true" aria-label="Select Market Scope">
          
          {/* 1. Global Search Header */}
          <div className="location-search-header">
            <div className="location-search-box">
              <IconSearch width={14} height={14} className="location-search-icon" />
              <input
                ref={searchInputRef}
                type="text"
                className="location-search-input"
                placeholder="Type district or state to filter instantly..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                aria-label="Filter district or state"
              />
              {searchQuery ? (
                <button
                  type="button"
                  className="location-search-clear"
                  onClick={() => setSearchQuery('')}
                  aria-label="Clear search"
                >
                  <IconClose width={12} height={12} />
                </button>
              ) : (
                <span className="location-search-hint"><kbd>/</kbd></span>
              )}
            </div>
          </div>

          {/* VIEW A: Search Results Overlay (When User Types) */}
          {searchResults !== null ? (
            <div className="location-search-results-list" role="listbox">
              {searchResults.length > 0 ? (
                <>
                  <div className="location-search-results-meta">
                    <span>MATCHING LOCATIONS</span>
                    <span>{searchResults.length} found</span>
                  </div>
                  {searchResults.map((item, idx) => {
                    const isSelected = selectedDistrict.toLowerCase() === item.code.toLowerCase();
                    const isFocused = focusedIndex === idx;
                    return (
                      <button
                        key={`${item.code}-${idx}`}
                        type="button"
                        className={`location-search-row ${isSelected ? 'is-selected' : ''} ${isFocused ? 'is-focused' : ''}`}
                        onClick={() => handleSelect(item.code)}
                        role="option"
                        aria-selected={isSelected}
                      >
                        <div className="search-row-icon">
                          {item.type === 'nation' ? (
                            <IconGlobe width={13} height={13} />
                          ) : (
                            <IconMapPin width={13} height={13} />
                          )}
                        </div>
                        <div className="search-row-label-wrap">
                          <span className="search-row-name">{item.name}</span>
                          <span className="search-row-state">{item.state}</span>
                        </div>
                        {isSelected && (
                          <span className="search-row-check">
                            <IconCheck width={14} height={14} />
                          </span>
                        )}
                      </button>
                    );
                  })}
                </>
              ) : (
                <div className="location-search-empty">
                  <span className="empty-emoji">🔍</span>
                  <span className="empty-text">No districts found matching "{searchQuery}"</span>
                  <button 
                    type="button" 
                    className="empty-reset-btn"
                    onClick={() => setSearchQuery('')}
                  >
                    Clear Search
                  </button>
                </div>
              )}
            </div>
          ) : (
            /* VIEW B: Two-Pane Master-Detail Region Hub */
            <div className="location-master-detail">
              
              {/* Left Pane: Vertical States Sidebar Rail */}
              <aside className="location-states-rail" aria-label="States List">
                <div className="states-rail-header">
                  <span className="states-rail-title">STATES</span>
                  <span className="states-rail-badge">{activeRegions.states?.length || 0}</span>
                </div>
                
                <div className="states-rail-scroll">
                  {(activeRegions.states || []).map((st) => {
                    const isActive = activeTab === st.code;
                    const cleanName = st.name.replace(' (All Districts)', '');
                    const badge = STATE_BADGES[st.code] || st.code.slice(0, 2).toUpperCase();
                    const count = st.districts?.length || 0;

                    return (
                      <button
                        key={st.code}
                        type="button"
                        className={`state-rail-item ${isActive ? 'is-active' : ''}`}
                        onClick={() => setActiveTab(st.code)}
                      >
                        <span className="state-rail-badge">{badge}</span>
                        <span className="state-rail-name">{cleanName}</span>
                        <span className="state-rail-count">{count}</span>
                      </button>
                    );
                  })}
                </div>
              </aside>

              {/* Right Pane: Districts Canvas */}
              <main className="location-districts-canvas" aria-label="Districts Grid">
                {currentActiveState && (
                  <>
                    {/* Top State Banner & "Statewide" Rollup CTA */}
                    <div className="canvas-state-banner">
                      <div className="canvas-banner-meta">
                        <h4 className="canvas-banner-title">
                          {currentActiveState.name.replace(' (All Districts)', '')}
                        </h4>
                        <span className="canvas-banner-subtitle">
                          {currentActiveState.districts.length} Industrial &amp; Commercial Districts
                        </span>
                      </div>

                      <button
                        type="button"
                        className={`canvas-statewide-btn ${selectedDistrict === currentActiveState.code ? 'is-selected' : ''}`}
                        onClick={() => handleSelect(currentActiveState.code)}
                        title={`Select all ${currentActiveState.districts.length} districts across ${currentActiveState.name.replace(' (All Districts)', '')}`}
                      >
                        {selectedDistrict === currentActiveState.code && (
                          <IconCheck width={12} height={12} className="statewide-check-icon" />
                        )}
                        <span>All {currentActiveState.name.replace(' (All Districts)', '')} (Statewide)</span>
                      </button>
                    </div>

                    {/* Interactive Districts Card Grid */}
                    <div className="canvas-districts-scroll">
                      <div className="canvas-districts-grid">
                        {currentActiveState.districts.map((d) => {
                          const isSelected = selectedDistrict.toLowerCase() === d.code.toLowerCase();
                          const formattedName = formatDistrictName(d.name);

                          return (
                            <button
                              key={d.code}
                              type="button"
                              className={`district-card-btn ${isSelected ? 'is-selected' : ''}`}
                              onClick={() => handleSelect(d.code)}
                              title={`${formattedName}, ${currentActiveState.name.replace(' (All Districts)', '')}`}
                            >
                              <span className="district-card-name">{formattedName}</span>
                              {isSelected ? (
                                <IconCheck width={13} height={13} className="district-check-active" />
                              ) : (
                                <span className="district-card-dot" />
                              )}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  </>
                )}
              </main>

            </div>
          )}

          {/* Bottom Popover Status Bar */}
          <footer className="location-popover-status-footer">
            <span className="status-footer-left">
              Current: <strong>{currentDistrictName}</strong>
            </span>
            <span className="status-footer-shortcut">
              Press <kbd>Esc</kbd> to close
            </span>
          </footer>

        </div>
      )}
    </div>
  );
}
